/**
 * Frontend Error Recovery and Retry Service
 *
 * Provides comprehensive error handling and automatic recovery mechanisms for:
 * - Network connectivity issues
 * - API endpoint failures
 * - WebSocket connection problems
 * - Authentication errors
 * - Rate limiting responses
 * - Service unavailability
 */

import MCP_API from '../api/mcp.api.js';
import { getLangGraphBackendUrl } from '../utils/langgraphConfig.js';

class ErrorRecoveryService {
  constructor() {
    this.retryQueue = new Map();
    this.activeRecoveries = new Set();
    this.recoveryStrategies = new Map();
    this.errorHistory = new Map();
    this.maxRetryHistory = 100;

    // Recovery state
    this.isRecovering = false;
    this.lastRecoveryAttempt = null;
    this.recoveryCount = 0;

    // Retry configuration
    this.defaultRetryConfig = {
      maxAttempts: 3,
      baseDelay: 1000,
      maxDelay: 30000,
      backoffMultiplier: 2,
      jitter: 0.1, // 10% jitter
      timeout: 30000,
    };

    this.initializeRecoveryStrategies();
  }

  /**
   * Initialize default recovery strategies for different error types
   */
  initializeRecoveryStrategies() {
    // Network connectivity recovery
    this.recoveryStrategies.set('network', async (error, context) => {
      console.log('🔄 Network recovery strategy triggered');

      // Check if we're online
      if (!navigator.onLine) {
        return new Promise((resolve) => {
          const handleOnline = () => {
            window.removeEventListener('online', handleOnline);
            console.log('✅ Network connectivity restored');
            resolve(true);
          };
          window.addEventListener('online', handleOnline);
        });
      }

      // Test connectivity by pinging the backend
      try {
        const response = await fetch(`${getLangGraphBackendUrl()}/health`, {
          method: 'GET',
          timeout: 5000,
        });
        return response.ok;
      } catch {
        return false;
      }
    });

    // WebSocket connection recovery
    this.recoveryStrategies.set('websocket', async (error, context) => {
      console.log('🔄 WebSocket recovery strategy triggered');

      try {
        // Try to reconnect WebSocket
        if (MCP_API.isWebSocketConnected()) {
          return true; // Already connected
        }

        await MCP_API.waitForConnection(10000);
        return MCP_API.isWebSocketConnected();
      } catch {
        return false;
      }
    });

    // Authentication recovery
    this.recoveryStrategies.set('auth', async (error, context) => {
      console.log('🔄 Authentication recovery strategy triggered');

      // Check if we have a valid token
      const token = localStorage.getItem('tkn');
      if (!token) {
        // Trigger login flow
        this.emit('auth_required');
        return false;
      }

      // Validate token
      try {
        const response = await fetch(`${getLangGraphBackendUrl()}/auth/validate`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          return true;
        }
        // Token invalid, clear it and require re-auth
        localStorage.removeItem('tkn');
        this.emit('auth_required');
        return false;
      } catch {
        return false;
      }
    });

    // Rate limiting recovery
    this.recoveryStrategies.set('rate_limit', async (error, context) => {
      console.log('🔄 Rate limit recovery strategy triggered');

      // Extract retry-after header if available
      const retryAfter = error.retryAfter || this.calculateBackoffDelay(context.attempt, context.config);
      console.log(`⏳ Rate limited. Waiting ${retryAfter}ms before retry`);

      await new Promise((resolve) => setTimeout(resolve, retryAfter));
      return true;
    });

    // Service unavailable recovery
    this.recoveryStrategies.set('service_unavailable', async (error, context) => {
      console.log('🔄 Service unavailable recovery strategy triggered');

      // Check service health
      try {
        const healthResponse = await fetch(`${getLangGraphBackendUrl()}/health`);
        const healthData = await healthResponse.json();

        if (healthData.overall_status === 'healthy') {
          return true;
        }
        // Wait for service to recover
        await new Promise((resolve) => setTimeout(resolve, 5000));
        return healthData.overall_status === 'healthy';
      } catch {
        // If health check fails, assume service is still down
        return false;
      }
    });

    // Generic retry with exponential backoff
    this.recoveryStrategies.set('retry', async (error, context) => {
      const delay = this.calculateBackoffDelay(context.attempt, context.config);
      console.log(`⏳ Generic retry strategy. Waiting ${delay}ms before retry ${context.attempt}/${context.config.maxAttempts}`);

      await new Promise((resolve) => setTimeout(resolve, delay));
      return true;
    });
  }

  /**
   * Categorize error type for appropriate recovery strategy
   */
  categorizeError(error, response = null) {
    if (!navigator.onLine) {
      return 'network';
    }

    if (response) {
      const { status } = response;

      if (status === 401 || status === 403) {
        return 'auth';
      }

      if (status === 429) {
        return 'rate_limit';
      }

      if (status >= 500) {
        return 'service_unavailable';
      }
    }

    // Check error message patterns
    const message = error.message?.toLowerCase() || '';

    if (message.includes('network') || message.includes('fetch')) {
      return 'network';
    }

    if (message.includes('websocket') || message.includes('connection')) {
      return 'websocket';
    }

    if (message.includes('unauthorized') || message.includes('forbidden')) {
      return 'auth';
    }

    return 'retry'; // Default to generic retry
  }

  /**
   * Calculate backoff delay with exponential backoff and jitter
   */
  calculateBackoffDelay(attempt, config) {
    const baseDelay = Math.min(
      config.baseDelay * config.backoffMultiplier ** (attempt - 1),
      config.maxDelay,
    );

    const jitter = Math.random() * config.jitter * baseDelay;
    return Math.floor(baseDelay + jitter);
  }

  /**
   * Execute operation with automatic error recovery
   */
  async executeWithRecovery(operation, context = {}) {
    const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const config = {
      ...this.defaultRetryConfig,
      ...context.config,
    };

    const operationContext = {
      ...context,
      operationId,
      config,
      attempt: 0,
      startTime: Date.now(),
    };

    return this._executeWithRetry(operation, operationContext);
  }

  async _executeWithRetry(operation, context) {
    context.attempt++;

    try {
      // Execute the operation
      const result = await Promise.race([
        operation(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Operation timeout')), context.config.timeout)),
      ]);

      // Success - cleanup error history
      this._recordSuccess(context);
      return result;
    } catch (error) {
      console.error(`❌ Operation failed (attempt ${context.attempt}):`, error);

      // Record the error
      this._recordError(context, error);

      // Check if we should retry
      if (context.attempt < context.config.maxAttempts) {
        const errorType = this.categorizeError(error, error.response);

        console.log(`🔄 Attempting recovery for error type: ${errorType}`);

        try {
          // Attempt recovery
          const recovered = await this._attemptRecovery(errorType, error, context);

          if (recovered) {
            console.log('✅ Recovery successful, retrying operation');
            return this._executeWithRetry(operation, context);
          }
          console.log('❌ Recovery failed, aborting');
        } catch (recoveryError) {
          console.error('❌ Recovery strategy failed:', recoveryError);
        }
      }

      // Max attempts reached or recovery failed
      throw error;
    }
  }

  async _attemptRecovery(errorType, error, context) {
    const strategy = this.recoveryStrategies.get(errorType);
    if (!strategy) {
      console.warn(`⚠️ No recovery strategy for error type: ${errorType}`);
      return false;
    }

    // Prevent concurrent recovery attempts
    if (this.activeRecoveries.has(errorType)) {
      console.log(`⏳ Recovery already in progress for ${errorType}, waiting...`);
      return new Promise((resolve) => {
        const checkRecovery = setInterval(() => {
          if (!this.activeRecoveries.has(errorType)) {
            clearInterval(checkRecovery);
            resolve(true);
          }
        }, 1000);
      });
    }

    this.activeRecoveries.add(errorType);

    try {
      const result = await strategy(error, context);
      return result;
    } finally {
      this.activeRecoveries.delete(errorType);
    }
  }

  _recordError(context, error) {
    const errorRecord = {
      operationId: context.operationId,
      attempt: context.attempt,
      error: error.message || error.toString(),
      timestamp: Date.now(),
      errorType: this.categorizeError(error, error.response),
    };

    // Store in history
    if (!this.errorHistory.has(context.operationId)) {
      this.errorHistory.set(context.operationId, []);
    }

    const operationHistory = this.errorHistory.get(context.operationId);
    operationHistory.push(errorRecord);

    // Keep only recent history
    if (operationHistory.length > this.maxRetryHistory) {
      operationHistory.shift();
    }

    console.log('📝 Error recorded:', errorRecord);
  }

  _recordSuccess(context) {
    console.log(`✅ Operation ${context.operationId} succeeded after ${context.attempt} attempts`);

    // Clean up error history
    this.errorHistory.delete(context.operationId);
  }

  /**
   * Get error recovery statistics
   */
  getRecoveryStats() {
    const stats = {
      totalRecoveries: this.recoveryCount,
      lastRecoveryAttempt: this.lastRecoveryAttempt,
      activeRecoveries: Array.from(this.activeRecoveries),
      errorHistorySize: this.errorHistory.size,
      recoveryStrategies: Array.from(this.recoveryStrategies.keys()),
    };

    return stats;
  }

  /**
   * Clear error history
   */
  clearErrorHistory() {
    this.errorHistory.clear();
    console.log('🧹 Error history cleared');
  }

  /**
   * Event system for recovery notifications
   */
  on(event, handler) {
    if (!this.eventListeners) {
      this.eventListeners = new Map();
    }
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(handler);
  }

  off(event, handler) {
    if (this.eventListeners && this.eventListeners.has(event)) {
      const handlers = this.eventListeners.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners && this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }
}

// Global error recovery service instance
export const errorRecoveryService = new ErrorRecoveryService();

// Convenience functions
export const executeWithRecovery = (operation, config = {}) => errorRecoveryService.executeWithRecovery(operation, { config });

export const getRecoveryStats = () => errorRecoveryService.getRecoveryStats();

export const clearErrorHistory = () => {
  errorRecoveryService.clearErrorHistory();
};

// Export for use in components
export default errorRecoveryService;
